/**
 * WebView 환경 감지 및 토큰 반환 유틸리티
 */

import { auth } from "../firebase";
// WebView 메시지 타입
interface WebViewMessage {
  type: string;
  token?: string;
  userInfo?: UserInfo | null;
  timestamp?: string;
  platform?: string;
  code?: string;
  url?: string;
  state?: NativeLoginShellState;
}

export type NativeLoginShellState =
  | "email"
  | "emailError"
  | "password"
  | "passwordError"
  | "create"
  | "createError"
  | "access";

export interface UserInfo {
  uid: string;
  email?: string | null;
  displayName?: string | null;
  provider?: string;
  isGuest?: boolean;
  login_method?: string;
  token?: string; // Optional token field for compatibility
}

const isGuestUserInfo = (userInfo: UserInfo | null): boolean => {
  if (!userInfo) return false;

  return (
    userInfo.isGuest === true ||
    userInfo.login_method === "guest" ||
    userInfo.provider === "guest"
  );
};

// WebView 환경 감지
export const isWebView = (): boolean => {
  const userAgent = navigator.userAgent.toLowerCase();

  // Apple Vision Pro WebView 감지
  const isVisionPro =
    userAgent.includes("visionos") ||
    userAgent.includes("vision pro") ||
    userAgent.includes("realityos");

  // 일반적인 WebView 감지
  const isWebView =
    userAgent.includes("wv") || // Android WebView
    userAgent.includes("webview") ||
    (userAgent.includes("mobile safari") && !userAgent.includes("chrome")) ||
    !!(window as unknown as { ReactNativeWebView?: unknown })
      .ReactNativeWebView; // React Native WebView

  // 추가 감지 방법들
  const hasWebKitMessageHandlers = !!(
    window as unknown as { webkit?: { messageHandlers?: unknown } }
  ).webkit?.messageHandlers;
  const isStandalone =
    (window.navigator as unknown as { standalone?: boolean }).standalone ===
    true;
  const hasParentWindow = window.parent !== window;

  // Apple Vision Pro 특별 감지 (User-Agent에 visionos가 없을 수 있음)
  const isAppleDevice =
    userAgent.includes("iphone") ||
    userAgent.includes("ipad") ||
    userAgent.includes("mac");

  // Apple Vision Pro WebView 특별 감지 (실제 환경 기반)
  // - Mac 기기에서 WebKit Message Handlers가 있으면 Vision Pro WebView로 간주
  const isAppleVisionProSpecial = isAppleDevice && hasWebKitMessageHandlers;

  // Apple Vision Pro WebView 강화 감지
  // - Mac 기기에서 WebKit이 있고, 특정 화면 크기나 환경이면 Vision Pro로 간주
  const isAppleVisionProEnhanced =
    isAppleDevice && !!(window as unknown as { webkit?: unknown }).webkit;

  if (process.env.NODE_ENV === "development") {
    console.log("🔍 WebView 감지 정보:", {
      userAgent: userAgent,
      isVisionPro,
      isWebView,
      hasWebKitMessageHandlers,
      isStandalone,
      hasParentWindow,
      isAppleDevice,
      isAppleVisionProSpecial,
      isAppleVisionProEnhanced,
      webkit: !!(window as unknown as { webkit?: unknown }).webkit,
      screenSize: `${window.screen.width}x${window.screen.height}`,
      windowSize: `${window.innerWidth}x${window.innerHeight}`,
      messageHandlers: (
        window as unknown as {
          webkit?: { messageHandlers?: Record<string, unknown> };
        }
      ).webkit?.messageHandlers
        ? Object.keys(
            (
              window as unknown as {
                webkit?: { messageHandlers?: Record<string, unknown> };
              }
            ).webkit?.messageHandlers || {},
          )
        : null,
    });
  }

  return (
    isVisionPro ||
    isWebView ||
    hasWebKitMessageHandlers ||
    isStandalone ||
    hasParentWindow ||
    isAppleVisionProSpecial ||
    isAppleVisionProEnhanced
  );
};

/**
 * ViewR 앱(visionOS 등) 로그인용 WKWebView — `authHandler` 메시지 핸들러가 있을 때만 true.
 * 일반 데스크톱 브라우저는 false (데스크톱은 `/pair` 직접 접근 불가, 대시보드 모달 사용).
 */
export const isViewRNativeAuthWebView = (): boolean => {
  const handlers = (
    window as unknown as {
      webkit?: { messageHandlers?: Record<string, unknown> };
    }
  ).webkit?.messageHandlers;
  return (
    !!handlers && Object.prototype.hasOwnProperty.call(handlers, "authHandler")
  );
};

/**
 * AVP 네이티브 로그인 WebView: React Router 이동 **직전**에 Swift에 알려
 * 창 리사이즈·스크림을 먼저 올려 한 프레임 깜빡임을 줄입니다.
 */
export const notifyLoginShellTransitionBegin = (targetHref: string): void => {
  try {
    const authHandler = (
      window as unknown as {
        webkit?: {
          messageHandlers?: {
            authHandler?: { postMessage: (msg: unknown) => void };
          };
        };
      }
    ).webkit?.messageHandlers?.authHandler;
    if (!authHandler?.postMessage) return;
    authHandler.postMessage({
      type: "SHELL_TRANSITION_BEGIN",
      href: targetHref,
    });
  } catch {
    /* noop */
  }
};

/**
 * AVP 로그인 React 상태가 바뀔 때 Figma의 상태별 canvas 크기를 SwiftUI shell에 전달합니다.
 * URL만으로 구분할 수 없는 email/password/create validation 상태를 위한 전용 계약입니다.
 */
export const notifyNativeLoginShellState = (
  state: NativeLoginShellState,
): void => {
  try {
    const authHandler = (
      window as unknown as {
        webkit?: {
          messageHandlers?: {
            authHandler?: { postMessage: (msg: WebViewMessage) => void };
          };
        };
      }
    ).webkit?.messageHandlers?.authHandler;
    if (!authHandler?.postMessage) return;
    authHandler.postMessage({
      type: "LOGIN_SHELL_STATE",
      state,
    });
  } catch {
    /* noop */
  }
};

// Apple Vision Pro 특화 감지
export const isAppleVisionPro = (): boolean => {
  const userAgent = navigator.userAgent.toLowerCase();

  // 기본 Vision Pro 감지
  const isVisionPro =
    userAgent.includes("visionos") ||
    userAgent.includes("vision pro") ||
    userAgent.includes("realityos");

  // Apple Vision Pro WebView 특별 감지 (실제 환경 기반)
  const isAppleDevice =
    userAgent.includes("iphone") ||
    userAgent.includes("ipad") ||
    userAgent.includes("mac");
  const hasWebKitMessageHandlers = !!(
    window as unknown as { webkit?: { messageHandlers?: unknown } }
  ).webkit?.messageHandlers;
  const isAppleVisionProSpecial = isAppleDevice && hasWebKitMessageHandlers;

  // Apple Vision Pro WebView 강화 감지
  const isAppleVisionProEnhanced =
    isAppleDevice &&
    !!(window as unknown as { webkit?: unknown }).webkit &&
    (window.screen.width <= 1000 || window.screen.height <= 600);

  return isVisionPro || isAppleVisionProSpecial || isAppleVisionProEnhanced;
};

// 현재 OS 플랫폼 감지
export const getCurrentPlatform = (): "visionos" | "webgl" => {
  const userAgent = navigator.userAgent.toLowerCase();

  // Apple Vision Pro 감지
  if (isAppleVisionPro()) {
    return "visionos";
  }

  // WebGL (일반 웹 브라우저) 감지
  if (
    userAgent.includes("chrome") ||
    userAgent.includes("firefox") ||
    userAgent.includes("safari") ||
    userAgent.includes("edge")
  ) {
    return "webgl";
  }

  // 기본값
  return "webgl";
};

// 플랫폼별 파일명 생성
export const generatePlatformFileName = (
  baseFileName: string,
  platform: "visionos" | "webgl",
): string => {
  const prefix = platform === "visionos" ? "visionos_" : "web_";
  const extension = ".vse";

  return `${prefix}${baseFileName}${extension}`;
};

// URL 파라미터에서 특정 값 가져오기
export const getUrlParameter = (name: string): string | null => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get(name);
};

// URL에 토큰 정보 추가
export const addTokenToUrl = (
  token: string,
  userInfo: UserInfo | null = null,
): string | null => {
  try {
    const url = new URL(window.location.href);

    // Guest 로그인 여부 판단
    const isGuestLogin = isGuestUserInfo(userInfo);

    // 토큰 추가 (Guest는 구분자 접두사 부여)
    const tokenForUrl = isGuestLogin ? `guest:${token}` : token;
    url.searchParams.set("token", tokenForUrl);

    // 사용자 정보가 있으면 추가
    if (userInfo) {
      url.searchParams.set("uid", userInfo.uid);
      url.searchParams.set("email", userInfo.email || "");
      url.searchParams.set("provider", userInfo.provider || "");
    }

    // 성공 상태 및 게스트 여부 추가
    url.searchParams.set("auth_status", "success");
    if (isGuestLogin) {
      url.searchParams.set("is_guest", "true");
    } else {
      url.searchParams.delete("is_guest");
    }

    // URL 변경 (히스토리 추가하지 않음)
    window.history.replaceState({}, "", url.toString());

    console.log("✅ WebView 토큰 URL 업데이트 완료:", url.toString());
    return url.toString();
  } catch (error) {
    console.error("❌ WebView 토큰 URL 업데이트 실패:", error);
    return null;
  }
};

// WebView에서 토큰을 부모 앱으로 전송 (postMessage 사용)
export const sendTokenToParent = (
  token: string,
  userInfo: UserInfo | null = null,
): boolean => {
  try {
    if (window.parent && window.parent !== window) {
      // Guest 로그인 여부 판단
      const isGuestLogin = isGuestUserInfo(userInfo);

      // userInfo에 isGuest 필드 추가
      const enhancedUserInfo = userInfo
        ? {
            ...userInfo,
            isGuest: isGuestLogin,
          }
        : null;

      const message: WebViewMessage = {
        type: "AUTH_SUCCESS",
        token: token,
        userInfo: enhancedUserInfo,
        timestamp: new Date().toISOString(),
      };

      window.parent.postMessage(message, "*");
      console.log("✅ WebView 부모 앱으로 토큰 전송 완료");
      return true;
    }
    return false;
  } catch (error) {
    console.error("❌ WebView 부모 앱 토큰 전송 실패:", error);
    return false;
  }
};

// Apple Vision Pro 특화 메시지 전송
export const sendMessageToVisionPro = (
  token: string | "auth_info",
  userInfo: UserInfo | null = null,
): boolean => {
  try {
    // Guest 로그인 여부 판단
    const isGuestLogin = isGuestUserInfo(userInfo);

    // userInfo에 isGuest 필드 추가
    const enhancedUserInfo = userInfo
      ? {
          ...userInfo,
          isGuest: isGuestLogin,
        }
      : null;

    // Apple Vision Pro에서 사용할 수 있는 다양한 메시지 전송 방법
    const message: WebViewMessage = {
      type: token === "auth_info" ? "AUTH_INFO" : "AUTH_SUCCESS",
      token: token === "auth_info" ? undefined : token,
      userInfo: enhancedUserInfo,
      timestamp: new Date().toISOString(),
      platform: navigator.userAgent.toLowerCase().includes("android")
        ? "android"
        : "visionos",
    };

    // 1. postMessage를 통한 전송
    if (window.parent && window.parent !== window) {
      window.parent.postMessage(message, "*");
    }

    // 2. window.webkit.messageHandlers를 통한 전송 (iOS/visionOS 네이티브 브리지)
    const webkit = (
      window as unknown as {
        webkit?: {
          messageHandlers?: Record<
            string,
            { postMessage: (msg: unknown) => void }
          >;
        };
      }
    ).webkit;
    if (webkit?.messageHandlers) {
      Object.keys(webkit.messageHandlers).forEach((handlerName) => {
        try {
          webkit.messageHandlers[handlerName].postMessage(message);
          console.log(
            `✅ Vision Pro 네이티브 핸들러로 메시지 전송: ${handlerName}`,
          );
        } catch (error) {
          console.log(`⚠️ 핸들러 ${handlerName} 전송 실패:`, error);
        }
      });
    }

    // 3. React Native WebView 브리지
    const reactNativeWebView = (
      window as unknown as {
        ReactNativeWebView?: { postMessage: (msg: string) => void };
      }
    ).ReactNativeWebView;
    if (reactNativeWebView) {
      reactNativeWebView.postMessage(JSON.stringify(message));
      console.log("✅ React Native WebView 브리지로 메시지 전송");
    }

    return true;
  } catch (error) {
    console.error("❌ Vision Pro 메시지 전송 실패:", error);
    return false;
  }
};

// 메시지 리스너 설정 (부모 앱으로부터 메시지 수신)
export const setupMessageListener = (
  callback: (data: WebViewMessage) => void,
): (() => void) => {
  const handleMessage = (event: MessageEvent) => {
    try {
      console.log("📨 WebView 메시지 수신:", event.data);

      if (event.data && typeof event.data === "object") {
        callback(event.data as WebViewMessage);
      }
    } catch (error) {
      console.error("❌ 메시지 처리 중 오류:", error);
    }
  };

  window.addEventListener("message", handleMessage);

  // 클린업 함수 반환
  return () => {
    window.removeEventListener("message", handleMessage);
  };
};

// Firebase Auth 상태 확인 및 토큰 반환
export const checkFirebaseAuthAndReturnToken = async (): Promise<{
  token: string;
  userInfo: UserInfo;
} | null> => {
  if (!isWebView()) {
    return null;
  }

  try {
    console.log("🔥 WebView Firebase Auth 상태 확인 중...");

    // 현재 Firebase 사용자 확인
    const currentUser = auth.currentUser;

    if (currentUser) {
      console.log("✅ Firebase 사용자 발견:", currentUser.email);

      const token = await currentUser.getIdToken();
      const userInfo: UserInfo = {
        uid: currentUser.uid,
        email: currentUser.email || null,
        displayName: currentUser.displayName || null,
        provider: currentUser.providerData[0]?.providerId || "unknown",
        isGuest: false, // Firebase Auth는 실제 사용자이므로 false
      };

      console.log("🥽 WebView Firebase Auth - 토큰 반환 시작");
      handleWebViewLoginSuccess(token, userInfo);

      return { token, userInfo };
    } else {
      console.log("⚠️ Firebase 사용자가 없음");
    }
  } catch (error) {
    console.error("❌ Firebase Auth 상태 확인 실패:", error);
  }

  return null;
};

// Firebase Auth 완료 후 강제 토큰 추출
export const forceExtractFirebaseToken = async (): Promise<{
  token: string;
  userInfo: UserInfo;
} | null> => {
  if (!isWebView()) {
    return null;
  }

  try {
    console.log("🔥 WebView Firebase Auth 강제 토큰 추출 시작...");

    // Firebase Auth 상태 확인
    const currentUser = auth.currentUser;

    if (currentUser) {
      console.log("✅ Firebase 사용자 발견:", currentUser.email);

      const token = await currentUser.getIdToken();
      const userInfo: UserInfo = {
        uid: currentUser.uid,
        email: currentUser.email || null,
        displayName: currentUser.displayName || null,
        provider: currentUser.providerData[0]?.providerId || "unknown",
        isGuest: false, // Firebase Auth는 실제 사용자이므로 false
      };

      console.log("🥽 WebView Firebase Auth - 강제 토큰 반환 시작");
      handleWebViewLoginSuccess(token, userInfo);

      return { token, userInfo };
    } else {
      console.log("⚠️ Firebase 사용자가 없음 - 강제 추출 시도");

      // Firebase Auth 상태 변경 감지 시도
      return new Promise((resolve) => {
        const unsubscribe = auth.onAuthStateChanged(async (user) => {
          if (user) {
            console.log("✅ Firebase Auth 상태 변경 감지:", user.email);

            const token = await user.getIdToken();
            const userInfo: UserInfo = {
              uid: user.uid,
              email: user.email || null,
              displayName: user.displayName || null,
              provider: user.providerData[0]?.providerId || "unknown",
              isGuest: false, // Firebase Auth는 실제 사용자이므로 false
            };

            console.log("🥽 WebView Firebase Auth - 상태 변경 토큰 반환");
            handleWebViewLoginSuccess(token, userInfo);

            unsubscribe();
            resolve({ token, userInfo });
          }
        });

        // 10초 후 타임아웃
        setTimeout(() => {
          console.log("⏰ Firebase Auth 상태 변경 타임아웃");
          unsubscribe();
          resolve(null);
        }, 10000);
      });
    }
  } catch (error) {
    console.error("❌ Firebase Auth 강제 토큰 추출 실패:", error);
    return null;
  }
};

// URL 모니터링 및 토큰 추출
export const monitorUrlForToken = (): {
  token: string;
  userInfo: UserInfo;
} | null => {
  // 현재 URL에서 토큰 확인
  const currentToken = getUrlParameter("token");
  const currentUid = getUrlParameter("uid");
  const currentEmail = getUrlParameter("email");
  const currentProvider = getUrlParameter("provider");
  const authStatus = getUrlParameter("auth_status");
  const isGuest = getUrlParameter("is_guest");

  if (currentToken && isWebView()) {
    console.log("🔍 URL에서 토큰 발견:", {
      token: currentToken.substring(0, 20) + "...",
      uid: currentUid,
      email: currentEmail,
      provider: currentProvider,
      authStatus,
      isGuest,
    });

    // 토큰이 이미 URL에 있으면 부모 앱으로 전송
    const userInfo: UserInfo = {
      uid: currentUid || "",
      email: currentEmail,
      provider: currentProvider || "",
      isGuest: isGuest === "true" || currentProvider === "guest",
      login_method: isGuest === "true" ? "guest" : undefined,
    };

    sendMessageToVisionPro(currentToken, userInfo);
    return { token: currentToken, userInfo };
  }

  return null;
};

// 페이지 로드 시 URL 모니터링 시작
export const startUrlMonitoring = (): (() => void) | undefined => {
  if (!isWebView()) {
    return undefined;
  }

  console.log("🔄 WebView URL 모니터링 시작");

  // 초기 URL 확인
  const initialToken = monitorUrlForToken();
  if (initialToken) {
    console.log("✅ 초기 URL에서 토큰 발견 및 전송 완료");
  }

  // 즉시 Firebase Auth 상태 확인 (Promise rejection 처리로 Uncaught 방지)
  checkFirebaseAuthAndReturnToken().catch((err) => {
    console.warn("WebView Firebase Auth 확인 실패:", err);
  });

  // 추가 Firebase Auth 상태 확인 (지연)
  setTimeout(() => {
    checkFirebaseAuthAndReturnToken().catch((err) => {
      console.warn("WebView Firebase Auth 지연 확인 실패:", err);
    });
  }, 2000);

  // 강제 토큰 추출 (더 긴 지연)
  setTimeout(() => {
    forceExtractFirebaseToken().catch((err) => {
      console.warn("WebView Firebase Auth 강제 추출 실패:", err);
    });
  }, 5000);

  // URL 변경 감지 (popstate 이벤트)
  const handleUrlChange = () => {
    setTimeout(() => {
      const token = monitorUrlForToken();
      if (token) {
        console.log("✅ URL 변경 시 토큰 발견 및 전송 완료");
      }
    }, 100);
  };

  window.addEventListener("popstate", handleUrlChange);

  // 클린업 함수 반환
  return () => {
    window.removeEventListener("popstate", handleUrlChange);
  };
};

// WebView 환경에서 로그인 완료 처리
export const handleWebViewLoginSuccess = (
  token: string,
  userInfo: UserInfo | null = null,
): void => {
  if (!isWebView()) {
    if (process.env.NODE_ENV === "development") {
      console.log("🔄 일반 브라우저 환경 - WebView 처리 건너뜀");
    }
    return;
  }

  console.log("🔄 WebView 환경 감지 - 토큰 반환 처리 시작");

  // URL에 토큰 추가
  const updatedUrl = addTokenToUrl(token, userInfo);

  // PICO도 같은 authHandler 계약을 사용하므로 기기 UA보다 브리지를 우선합니다.
  if (isViewRNativeAuthWebView() || isAppleVisionPro()) {
    console.log("🔄 네이티브 인증 브리지로 전송 시작");
    const visionProSent = sendMessageToVisionPro(token, userInfo);

    if (visionProSent) {
      console.log("✅ 네이티브 인증 토큰 전송 완료");
    }
  } else {
    // 일반 WebView 처리
    const messageSent = sendTokenToParent(token, userInfo);

    if (messageSent) {
      console.log("✅ 일반 WebView 토큰 전송 완료");
    }
  }

  if (updatedUrl) {
    console.log("✅ WebView 토큰 반환 완료");
  } else {
    console.warn("⚠️ WebView 토큰 반환 실패");
  }
};

// URL에서 Firebase Auth 관련 정보 추출
interface AuthInfo {
  type: "firebase_auth_code" | "google_oauth_code";
  code: string;
  state?: string | null;
  scope?: string | null;
  url: string;
}

export const extractAuthInfoFromUrl = (): AuthInfo | null => {
  try {
    const url = window.location.href;
    console.log("🔍 URL에서 Auth 정보 추출 시도:", url);

    // Firebase Auth 핸들러 URL에서 정보 추출
    if (url.includes("/__/auth/handler")) {
      const urlParams = new URLSearchParams(window.location.search);
      const state = urlParams.get("state");
      const code = urlParams.get("code");
      const scope = urlParams.get("scope");

      if (code) {
        console.log(
          "🔐 Firebase Auth 코드 발견:",
          code.substring(0, 20) + "...",
        );
        return {
          type: "firebase_auth_code",
          code: code,
          state: state,
          scope: scope,
          url: url,
        };
      }
    }

    // OAuth 코드 추출
    if (url.includes("accounts.google.com/o/oauth2/auth")) {
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get("code");

      if (code) {
        console.log(
          "🔐 Google OAuth 코드 발견:",
          code.substring(0, 20) + "...",
        );
        return {
          type: "google_oauth_code",
          code: code,
          url: url,
        };
      }
    }

    return null;
  } catch (error) {
    console.error("❌ URL에서 Auth 정보 추출 실패:", error);
    return null;
  }
};

// URL 변경 감지 및 Auth 정보 추출
export const monitorUrlForAuthInfo = (): (() => void) => {
  let lastUrl = window.location.href;

  const checkUrl = () => {
    const currentUrl = window.location.href;

    if (currentUrl !== lastUrl) {
      console.log("🔄 URL 변경 감지:", currentUrl);
      lastUrl = currentUrl;

      // Auth 정보 추출
      const authInfo = extractAuthInfoFromUrl();
      if (authInfo) {
        console.log("✅ URL 변경 시 Auth 정보 발견:", authInfo.type);

        // WebView 환경에서 부모 앱으로 전송
        if (isWebView()) {
          sendMessageToVisionPro("auth_info", {
            uid: "",
            email: null,
            provider: authInfo.type,
          });
        }
      }
    }
  };

  // 초기 확인
  checkUrl();

  // 주기적 확인 (JavaScript 실행이 제한된 환경 대응)
  const interval = setInterval(checkUrl, 1000);

  // 클린업 함수 반환
  return () => {
    clearInterval(interval);
  };
};
